import type {
  ChangeEvent,
  ElementType,
  FocusEvent,
  FunctionComponent,
  HTMLProps,
  KeyboardEvent,
  RefObject,
} from 'react'
import React from 'react'

import classnames from 'classnames'

import styles from './Input.module.scss'

export const InputV2: FunctionComponent<Props> = ({
  name,
  id = name,
  onBlur,
  onChange,
  inputRef,
  value,
  className,
  type = 'text',
  disabled = false,
  placeholder = '',
  required = false,
  autoFocus = false,
  hasErrors,
  icon: Icon,
  aria,
  autoComplete,
  ...rest
}) => {
  const hasIcon = !!Icon
  const {
    ariaLabel = '',
    ariaDescribedBy = '',
    ariaControls = '',
    ariaInvalid = undefined,
  } = aria ?? {}

  return (
    <div className={styles.wrapper}>
      <input
        {...(ariaLabel && { 'aria-label': ariaLabel })}
        {...(ariaDescribedBy && { 'aria-describedby': ariaDescribedBy })}
        {...(ariaControls && { 'aria-controls': ariaControls })}
        {...(ariaInvalid !== undefined && { 'aria-invalid': ariaInvalid })}
        {...(autoComplete && { autoComplete })}
        // eslint-disable-next-line jsx-a11y/no-autofocus
        autoFocus={autoFocus}
        className={classnames(
          styles.wrapperInput,
          {
            [styles.wrapperInputWithIcon]: hasIcon,
            [styles.wrapperInputHasErrors]: hasErrors,
          },
          className
        )}
        data-testid={`input-${name}`}
        disabled={disabled}
        id={id}
        name={name}
        onBlur={onBlur}
        onChange={onChange}
        placeholder={placeholder}
        ref={inputRef}
        required={required}
        type={type}
        value={value}
        {...rest}
      />
      {hasIcon && <Icon className={styles.wrapperInputIcon} />}
    </div>
  )
}

///////// IMPLEMENTATION /////////

export type Props = {
  /**
   * <a href="https://developer.mozilla.org/en-US/docs/Web/Accessibility/ARIA/Attributes">Aria</a> object, add properties as needed.
   */
  aria?: {
    ariaLabel?: string
    ariaDescribedBy?: string
    ariaControls?: string
    ariaInvalid?: boolean
  } | null
  /**
   * Optional autoFocus option to focus the input element.
   */
  autoFocus?: boolean
  /**
   * <a href="https://developer.mozilla.org/en-US/docs/Web/HTML/Attributes/autocomplete">Autocomplete</a>, used in some cases on website.
   */
  autoComplete?: string
  /**
   * Class name that can be added to add extra styling to the component.
   */
  className?: string
  /**
   * Optional disabled attribute to prevent user changes.
   */
  disabled?: boolean
  /**
   * Name for the input element.
   */
  name: string
  /**
   * onChange handler which passes the event target value to be used in consuming application.
   */
  onChange: (target: ChangeEvent<HTMLInputElement>) => void
  /**
   * onBlur handler.
   */
  onBlur?: (target: FocusEvent<HTMLInputElement>) => void
  /**
   * onFocus handler.
   */
  onFocus?: (target: FocusEvent<HTMLInputElement>) => void
  /**
   * onKeyDown handler.
   */
  onKeyDown?: (target: KeyboardEvent<HTMLInputElement>) => void
  /**
   * Optional navigational icon.
   */
  icon?: ElementType
  /**
   * Optional unique id for the input element, uses name if nothing is provided.
   */
  id?: string
  /**
   * Renders input error state when true.
   */
  hasErrors?: boolean
  /**
   * Value for the input element.
   */
  value: string | number
  /**
   * Optional placeholder text to display example input value.
   */
  placeholder?: string
  /**
   * Optional required value that appends the attribute.
   */
  required?: boolean
  /**
   * Optional ref to be forwarded to the input element.
   */
  inputRef?: RefObject<HTMLInputElement>
  /**
   * Optional type attribute, for radio and checkbox check respective components.
   */
  type?: InputTypes
  /**
   *
   * Used to identify a DOM node for testing purposes, default to `input-${name}`
   */
  dataTestId?: string
} & HTMLProps<HTMLInputElement>

export type InputTypes =
  | 'email'
  | 'file'
  | 'number'
  | 'password'
  | 'tel'
  | 'text'
